import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Input,
    Button,
    Form,
    Spin,
    App,
    Row,
    Col,
    Select
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { fiscalCashRegisterService } from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

const { Title } = Typography;

const FiscalCashRegisterCreate = () => {
    const navigate = useNavigate();
    const { message } = App.useApp();
    const { setPageTitle, setRightContent } = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        type: []
    });
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        fetchFormData();
        setPageTitle('Создание фискальной кассы');

        setRightContent(
            <Button
                type="primary"
                onClick={handleSave}
                loading={saving}
            >
                Создать
            </Button>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    const fetchFormData = async () => {
        try {
            setLoading(true);
            // Получаем данные для формы
            const formDataResponse = await fiscalCashRegisterService.getFormData();

            if (formDataResponse.status === 'success') {
                setFormData(formDataResponse.data);
                console.log('Получены данные для формы:', formDataResponse.data);
            } else {
                message.error('Не удалось загрузить данные для формы');
                navigate('/fiscal-cash-registers');
            }
        } catch (error) {
            console.error('Ошибка при загрузке данных:', error);
            message.error('Ошибка при загрузке данных для формы');
            navigate('/fiscal-cash-registers');
        } finally {
            setLoading(false);
        }
    };

    // Обработка сохранения данных фискальной кассы
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Получаем данные пользователя из localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const username = userData.username || '';
            const firstName = userData.first_name || '';
            const lastName = userData.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values,
                username: username,
                full_name: fullName
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await fiscalCashRegisterService.createFiscalCashRegister(
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Фискальная касса успешно создана');
                // Переходим на страницу созданной фискальной кассы
                navigate(`/fiscal-cash-registers/${response.data.fiscal_cash_register.id}`);
            } else {
                message.error(response.message || 'Ошибка при создании фискальной кассы');
            }
        } catch (error) {
            console.error('Ошибка при создании:', error);
            message.error(error.message || 'Ошибка при создании фискальной кассы');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Card>
            <Title level={3} style={{ marginTop: 0 }}>
                Создание новой фискальной кассы
            </Title>

            <Form
                form={form}
                layout="horizontal"
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Основные атрибуты" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Название"
                                name="name"
                                rules={[
                                    { required: true, message: 'Введите название фискальной кассы' },
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите название фискальной кассы" maxLength={255} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Внешний ID"
                                name="external_id"
                                rules={[
                                    { required: true, message: 'Введите внешний ID' }
                                ]}
                            >
                                <Input placeholder="Введите внешний ID" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Тип"
                                name="type"
                                rules={[
                                    { required: true, message: 'Выберите тип фискальной кассы' }
                                ]}
                            >
                                <Select
                                    options={formData.type.map(type => ({
                                        label: type.label,
                                        value: type.value
                                    }))}
                                    placeholder="Выберите тип фискальной кассы"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Серийный номер ККМ"
                                name="kkm_serial_number"
                                rules={[
                                    { required: true, message: 'Введите серийный номер ККМ' }
                                ]}
                            >
                                <Input placeholder="Введите серийный номер ККМ" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Налоговый номер ККМ"
                                name="kkm_tax_register_number"
                                rules={[
                                    { required: true, message: 'Введите налоговый номер ККМ' }
                                ]}
                            >
                                <Input placeholder="Введите налоговый номер ККМ" />
                            </HorizontalFormItem>
                        </Card>

                        <Card title="Интеграция" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Логин интеграции"
                                name="integration_login"
                                rules={[
                                    { required: true, message: 'Введите логин интеграции' }
                                ]}
                            >
                                <Input placeholder="Введите логин интеграции" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Пароль интеграции"
                                name="integration_password"
                                rules={[
                                    { required: true, message: 'Введите пароль интеграции' }
                                ]}
                            >
                                <Input.Password placeholder="Введите пароль интеграции" />
                            </HorizontalFormItem>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card title="Данные для чека" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Наименование ОФД"
                                name="ofd_name"
                                rules={[
                                    { required: true, message: 'Введите наименование ОФД' }
                                ]}
                            >
                                <Input placeholder="Введите наименование ОФД" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Сайт ОФД"
                                name="ofd_site"
                                rules={[
                                    { required: true, message: 'Введите сайт ОФД' }
                                ]}
                            >
                                <Input placeholder="Введите сайт ОФД" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Наименование компании"
                                name="company_name"
                                rules={[
                                    { required: true, message: 'Введите наименование компании' }
                                ]}
                            >
                                <Input placeholder="Введите наименование компании" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Налоговый номер компании"
                                name="company_tax_id"
                                rules={[
                                    { required: true, message: 'Введите налоговый номер компании' }
                                ]}
                            >
                                <Input placeholder="Введите налоговый номер компании" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Адрес компании"
                                name="company_address"
                                rules={[
                                    { required: true, message: 'Введите адрес компании' }
                                ]}
                            >
                                <Input placeholder="Введите адрес компании" />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default FiscalCashRegisterCreate;